import {Card, Modal, Spinner} from 'react-bootstrap'
import {useState, useEffect} from 'react'
import {useLazyQuery} from '@apollo/client'
import {GET_AGENTS_LEADERBOARD} from '../../../gql/queries/reportsQuery'

interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
  avatar?: string
}

interface IAgentLeaderboardEntry {
  user: IUser
  policiesCount: number
  applicantsCount: number
  rank: number
}

interface IAgentsLeaderboardProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  loading?: boolean
}

interface IResLeaderboard {
  agentsLeaderboard: IAgentLeaderboardEntry[]
}

type SortField = 'agent' | 'policies' | 'applicants'
type SortOrder = 'asc' | 'desc'

export const AgentsLeaderboard = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  loading: parentLoading,
}: IAgentsLeaderboardProps) => {
  const [showModal, setShowModal] = useState(false)
  const [sortField, setSortField] = useState<SortField>('policies')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getFullLeaderboard, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResLeaderboard>(GET_AGENTS_LEADERBOARD, {
      fetchPolicy: 'no-cache',
    })

  const [getTopLeaderboard, {data: topData, loading: loadingTop}] =
    useLazyQuery<IResLeaderboard>(GET_AGENTS_LEADERBOARD, {
      fetchPolicy: 'no-cache',
    })

  // Fetch top 5 on mount/filter change
  useEffect(() => {
    getTopLeaderboard({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        limit: 5,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, getTopLeaderboard])

  const handleShowMore = () => {
    getFullLeaderboard({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'agent' ? 'asc' : 'desc')
    }
  }

  const leaderboardData = topData?.agentsLeaderboard || []
  const fullLeaderboardData = fullData?.agentsLeaderboard || []

  const sortedFullLeaderboard = [...fullLeaderboardData].sort((a, b) => {
    if (sortField === 'agent') {
      const nameA = `${a.user.firstName} ${a.user.lastName}`
      const nameB = `${b.user.firstName} ${b.user.lastName}`
      const comparison = nameA.localeCompare(nameB)
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'policies') {
      const comparison = a.policiesCount - b.policiesCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.applicantsCount - b.applicantsCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const getMedalIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇'
      case 2:
        return '🥈'
      case 3:
        return '🥉'
      default:
        return ''
    }
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const renderAgentRow = (entry: IAgentLeaderboardEntry, isModal: boolean = false) => (
    <tr key={entry.user.id}>
      <td className='ps-0'>
        <div className='d-flex align-items-center'>
          <div className='me-3'>
            {entry.user.avatar ? (
              <img
                src={entry.user.avatar}
                alt={`${entry.user.firstName} ${entry.user.lastName}`}
                className='rounded-circle'
                style={{width: 40, height: 40, objectFit: 'cover'}}
              />
            ) : (
              <div
                className='rounded-circle bg-light-primary d-flex align-items-center justify-content-center'
                style={{width: 40, height: 40}}
              >
                <span className='text-primary fw-bold fs-6'>
                  {getInitials(entry.user.firstName, entry.user.lastName)}
                </span>
              </div>
            )}
          </div>
          <div>
            <div className='d-flex align-items-center'>
              <span className='text-gray-800 fw-bold fs-6 me-2'>
                {entry.user.firstName} {entry.user.lastName}
              </span>
              {entry.rank <= 3 && <span className='fs-5'>{getMedalIcon(entry.rank)}</span>}
            </div>
            <span className='text-muted fs-8'>
              {entry.rank === 1 ? '1st' : entry.rank === 2 ? '2nd' : entry.rank === 3 ? '3rd' : `${entry.rank}th`} place
            </span>
          </div>
        </div>
      </td>
      {isModal && (
        <td className='text-center'>
          <span className='text-muted fs-7'>{entry.user.email}</span>
        </td>
      )}
      <td className='text-center'>
        <div className='d-flex align-items-center justify-content-center'>
          <i className='bi bi-shield-check text-warning me-2 fs-4'></i>
          <span className='fw-bold text-gray-800 fs-5'>{formatNumber(entry.policiesCount)}</span>
        </div>
      </td>
      <td className='text-center pe-0'>
        <div className='d-flex align-items-center justify-content-center'>
          <i className='bi bi-people text-info me-2 fs-4'></i>
          <span className='fw-bold text-gray-800 fs-5'>{formatNumber(entry.applicantsCount)}</span>
        </div>
      </td>
    </tr>
  )

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Agents leaderboard (Top 5)</span>
            <span className='text-muted fw-semibold fs-7'>
              Agents with most sales this {dateCriteria === 'submitted' ? 'month' : 'period'}
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loadingTop || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              See more
            </button>
          </div>
        </Card.Header>
        <Card.Body className='pt-0'>
          {(loadingTop || parentLoading) && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 300}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingTop && !parentLoading && leaderboardData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 300}}>
              <p className='text-muted'>No data available for the selected period</p>
            </div>
          )}
          {!loadingTop && !parentLoading && leaderboardData.length > 0 && (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0'>Agent</th>
                    <th className='text-center'>Policies</th>
                    <th className='text-center pe-0'>Applicants</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry) => renderAgentRow(entry))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for full leaderboard */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Agents leaderboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullLeaderboardData.length === 0 && (
            <div className='text-center text-muted py-5'>No data available</div>
          )}
          {!loadingFull && fullLeaderboardData.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th
                      className='ps-0'
                      style={{cursor: 'pointer'}}
                      onClick={() => handleSort('agent')}
                    >
                      Agent {getSortIcon('agent')}
                    </th>
                    <th className='text-center'>Email</th>
                    <th
                      className='text-center'
                      style={{cursor: 'pointer'}}
                      onClick={() => handleSort('policies')}
                    >
                      Policies {getSortIcon('policies')}
                    </th>
                    <th
                      className='text-center pe-0'
                      style={{cursor: 'pointer'}}
                      onClick={() => handleSort('applicants')}
                    >
                      Applicants {getSortIcon('applicants')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullLeaderboard.map((entry) => renderAgentRow(entry, true))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
